import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Drawer from '@mui/material/Drawer';
import { Switch } from '@mui/material';
import {
  LIST_DEFAULT_ROWS_PER_PAGE,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  SearchField,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';

import { treatmentCatalogAPI } from '@/modules/treatment-catalog/api/treatmentCatalog.api';
import { treatmentUomAPI } from '@/modules/treatment-catalog/api/treatmentUom.api';
import type { TreatmentCatalogItem } from '@/modules/treatment-catalog/types/treatmentCatalog.types';
import {
  getCategoryName,
  getUomLabel,
  toUomOptions,
} from '@/modules/treatment-catalog/types/treatmentCatalog.types';
import {
  filterCatalogItems,
  getCatalogCategories,
} from '@/modules/treatment-catalog/utils/filterCatalogItems';
import { useCurrency } from '@/shared/context/CurrencyContext';
import { queryKeys } from '@/shared/query';
import { treatmentCategoryAPI } from '@/modules/treatment-catalog/api/treatmentCategory.api';

interface CatalogItemPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: TreatmentCatalogItem) => void;
  items?: TreatmentCatalogItem[];
  title?: string;
  selectLabel?: string;
  emptyMessage?: string;
  /** Keep inactive rows visible (copy-from-protocol). Default hides them. */
  includeInactive?: boolean;
  /** Hide the service currently being edited. */
  excludeItemIds?: string[];
  /** Only rows that already have procedure steps. */
  requireProcedureSteps?: boolean;
  /** Drawer (copy steps) vs centered dialog (other pickers). */
  presentation?: 'dialog' | 'drawer';
  /** Hide “only with procedure steps” filter (e.g. diagnosis suggested services). */
  showProcedureStepsFilter?: boolean;
  /** Close picker after each row action. False = add many then close manually. */
  closeOnSelect?: boolean;
  /** Disable row “add” actions (e.g. parent at max selection). */
  selectDisabled?: boolean;
}

const catalogItemHasProcedureSteps = (item: TreatmentCatalogItem): boolean =>
  (item.procedureSteps ?? []).some((step) => Boolean(step.title?.trim()));

const CatalogItemPickerDialog: React.FC<CatalogItemPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  items: externalItems,
  title,
  selectLabel,
  emptyMessage,
  includeInactive = false,
  excludeItemIds,
  requireProcedureSteps = false,
  presentation = 'dialog',
  showProcedureStepsFilter = true,
  closeOnSelect = true,
  selectDisabled = false,
}) => {
  const { t } = useTranslation(['treatment', 'common']);
  const { formatCurrency } = useCurrency();
  const isDrawer = presentation === 'drawer';
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(LIST_DEFAULT_ROWS_PER_PAGE);
  const [onlyWithProcedures, setOnlyWithProcedures] = useState(requireProcedureSteps);

  const catalogQuery = useQuery({
    queryKey: queryKeys.treatmentCatalog.list({}),
    queryFn: async () => (await treatmentCatalogAPI.getAll()).data ?? [],
    enabled: open && !externalItems,
  });
  const uomOptionsQuery = useQuery({
    queryKey: queryKeys.treatmentCatalog.uoms({ includeInactive: true }),
    queryFn: async () => {
      const res = await treatmentUomAPI.getAll(true);
      return toUomOptions(res.data ?? []);
    },
    enabled: open && !isDrawer,
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.treatmentCatalog.categories({}),
    queryFn: async () => {
      const res = await treatmentCategoryAPI.getAll(false);
      return res.data ?? [];
    },
    enabled: open,
  });

  const catalogItems = externalItems ?? catalogQuery.data ?? [];
  const uomOptions = uomOptionsQuery.data ?? [];
  const loading = !externalItems && catalogQuery.isPending;
  const error = !externalItems && catalogQuery.isError ? t('toasts.loadCatalogFailed') : null;

  useEffect(() => {
    if (!externalItems && catalogQuery.isError) {
      toast.error(t('toasts.loadCatalogFailed'));
    }
  }, [externalItems, catalogQuery.isError, t]);

  useEffect(() => {
    if (open) setOnlyWithProcedures(requireProcedureSteps);
  }, [open, requireProcedureSteps]);

  const resetFilters = (): void => {
    setSearchTerm('');
    setCategoryFilter('all');
    setOnlyWithProcedures(requireProcedureSteps);
    setPage(0);
  };

  const handleClose = (): void => {
    resetFilters();
    onClose();
  };

  const visibleItems = useMemo(() => {
    const excluded = new Set((excludeItemIds ?? []).filter(Boolean));
    return catalogItems.filter((item) => {
      if (!includeInactive && !item.isActive) return false;
      if (excluded.has(item._id) || (item.id && excluded.has(item.id))) return false;
      if (onlyWithProcedures && !catalogItemHasProcedureSteps(item)) {
        return false;
      }
      return true;
    });
  }, [catalogItems, excludeItemIds, includeInactive, onlyWithProcedures]);

  const filteredItems = useMemo(
    () =>
      filterCatalogItems(visibleItems, {
        searchTerm,
        categoryFilter,
      }),
    [visibleItems, searchTerm, categoryFilter]
  );

  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const categories = useMemo(() => {
    if (categoriesQuery.data?.length) {
      return categoriesQuery.data.map((category) => ({
        id: category.id,
        name: category.name,
      }));
    }
    return getCatalogCategories(visibleItems);
  }, [categoriesQuery.data, visibleItems]);

  const handleSelect = (item: TreatmentCatalogItem): void => {
    onSelect(item);
    if (closeOnSelect) {
      resetFilters();
      onClose();
    }
  };

  const pickerTitle = title ?? t('picker.title');
  const emptyColSpan = isDrawer ? 5 : 7;

  const toolbar = (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: isDrawer ? 0 : 2, alignItems: 'center' }}>
      <SearchField
        placeholder={t('picker.searchServices')}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sx={{ flex: '1 1 220px', minWidth: 200 }}
      />
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(String(e.target.value));
            setPage(0);
          }}
        >
          <MenuItem value="all">{t('categories.all')}</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showProcedureStepsFilter ? (
        <FormControlLabel
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          control={
            <Switch
              size="small"
              checked={onlyWithProcedures}
              onChange={(e) => {
                setOnlyWithProcedures(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={t('picker.withProcedureSteps')}
        />
      ) : null}
    </Box>
  );

  const tableHead = isDrawer ? (
    <TableRow>
      <TableCell>{t('fields.name')}</TableCell>
      <TableCell>{t('fields.code')}</TableCell>
      <TableCell>{t('fields.category')}</TableCell>
      <TableCell align="right">{t('picker.price')}</TableCell>
      <TableCell align="center" aria-hidden sx={{ width: 120, p: 0 }} />
    </TableRow>
  ) : (
    <TableRow>
      <TableCell>{t('picker.name')}</TableCell>
      <TableCell>{t('picker.code')}</TableCell>
      <TableCell>{t('picker.category')}</TableCell>
      <TableCell>{t('picker.uom')}</TableCell>
      <TableCell align="right">{t('picker.price')}</TableCell>
      <TableCell align="right">{t('picker.duration')}</TableCell>
      <TableCell align="center">{t('picker.action')}</TableCell>
    </TableRow>
  );

  const tableBody =
    paginatedItems.length > 0 ? (
      paginatedItems.map((item) => (
        <TableRow key={item._id} hover>
          <TableCell>
            <Typography variant="body2" fontWeight="medium">
              {item.name}
              {!item.isActive ? ` (${t('categoryCatalog.status.inactive')})` : ''}
            </Typography>
          </TableCell>
          <TableCell>{item.code}</TableCell>
          <TableCell>{getCategoryName(item.category)}</TableCell>
          {!isDrawer ? (
            <>
              <TableCell>{getUomLabel(item.uom, uomOptions)}</TableCell>
              <TableCell align="right">{formatCurrency(item.price)}</TableCell>
              <TableCell align="right">
                {t('catalog.durationMinutes', { count: item.duration })}
              </TableCell>
            </>
          ) : (
            <TableCell align="right">{formatCurrency(item.price)}</TableCell>
          )}
          <TableCell align="center">
            <Button
              size="small"
              intent="primary"
              disabled={selectDisabled}
              onClick={() => handleSelect(item)}
            >
              {selectLabel ?? t('common:add')}
            </Button>
          </TableCell>
        </TableRow>
      ))
    ) : (
      <TableRow>
        <TableCell colSpan={emptyColSpan} align="center">
          {onlyWithProcedures ? emptyMessage ?? t('picker.noServicesFound') : t('picker.noServicesFound')}
        </TableCell>
      </TableRow>
    );

  const dataSection = loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <TableContainer sx={isDrawer ? { flex: 1, minHeight: 0, overflow: 'auto', boxShadow: 'none' } : undefined}>
        <Table size="small" stickyHeader={isDrawer}>
          <TableHead>{tableHead}</TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Box>
    </>
  );

  if (isDrawer) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 720 },
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
            <Typography variant="h6">{pickerTitle}</Typography>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
            {error ? (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {error}
              </Alert>
            ) : null}
            {toolbar}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: 2, pb: 1 }}>
            {dataSection}
          </Box>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-end',
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button intent="secondary" onClick={handleClose}>
              {t('common:close')}
            </Button>
          </Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{pickerTitle}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {toolbar}
        {dataSection}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common:close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogItemPickerDialog;
